/**
 * Indopoin × Nevi — shared brand & copy (mobile + desktop).
 * Satu sumber kebenaran untuk nama, tagline, nav, CTA.
 */
export const brand = {
  company: 'Indopoin',
  ai: 'Nevi',
  product: 'Indopoin',
  poweredBy: 'Powered by Nevi',
  /** Slogan utama — dipakai topbar / header */
  tagline: 'Biarkan Nevi yang analisis',
  /** Slogan sekunder (desk) */
  slogan: 'Cerdas membaca market, tenang mengambil keputusan',
  deskLabel: 'Desk',
  pwaShort: 'Indopoin',
  fullLine: 'Indopoin · Powered by Nevi',
} as const;

/** Mobile UI copy */
export const copy = {
  headerTitle: brand.company,
  headerSub: brand.poweredBy,

  nav: {
    home: 'Ringkas',
    market: 'Market',
    nevi: 'Nevi',
    positions: 'Posisi',
    settings: 'Setelan',
  },

  home: {
    title: 'Ringkasan desk',
    sub: 'Nevi memantau market & gate DCD',
    boardTitle: 'NEVI WATCH',
    boardStandby: 'SIAGA',
    boardRunning: 'NEVI AKTIF',
    cta: 'Biarkan Nevi analisis',
    ctaRunning: 'Nevi sedang menganalisis…',
    recTitle: 'Rekomendasi Nevi',
    recEmpty: 'Belum ada hasil. Biarkan Nevi analisis dulu.',
    logTitle: 'Catatan Nevi',
    logEmpty: 'Belum ada catatan analisis.',
    statsPass: 'Layak',
    statsReject: 'Tidak lolos',
    statsTotal: 'Total',
  },

  market: {
    title: 'Konteks market',
    sub: 'Data untuk keputusan DCD',
    chartOffline: 'Chart belum tersedia',
    macroTitle: 'Event makro',
    maxPain: 'Max Pain',
  },

  nevi: {
    title: 'Rekomendasi Nevi',
    sub: 'Filter gate · soft score · cycle terakhir',
    tabPass: 'Layak',
    tabReject: 'Tidak lolos',
    emptyPass: 'Tidak ada setup yang lolos gate.',
    emptyReject: 'Tidak ada yang ditolak di cycle ini.',
  },

  positions: {
    title: 'Posisi',
    sub: 'Portfolio DCD Binance — angka penuh',
    sync: 'Sinkron dari Binance',
    syncing: 'Sedang menyinkronkan…',
    empty: 'Tidak ada posisi di bulan ini.',
  },

  settings: {
    title: 'Setelan',
    sub: 'Tampilan HP · Indopoin desk',
    desktopNote:
      'Threshold Nevi, API key, dan jadwal diatur di desktop. Di HP hanya opsi tampilan.',
  },

  fab: {
    idle: 'Minta Nevi analisis',
    running: 'Nevi menganalisis…',
  },
} as const;

/** Desktop shell + page copy */
export const desktop = {
  sidebarTitle: brand.company,
  sidebarSub: brand.tagline,
  topbarTitle: brand.company,
  topbarBadge: brand.ai,
  /** Slogan di topbar (bukan disclaimer) */
  topbarHint: brand.tagline,
  footer: `v0.1 · ${brand.company} SSO · ${brand.ai}`,

  sections: {
    main: 'Utama',
    data: 'Data market',
    nevi: 'Nevi & posisi',
    config: 'Konfigurasi',
    ref: 'Referensi',
  },

  nav: [
    { to: '/', label: 'Ringkasan', section: 'main', hint: 'Desk + minta Nevi analisis' },
    { to: '/market', label: 'Market', section: 'data', hint: 'Spot, DVOL, funding' },
    { to: '/kalender', label: 'Kalender', section: 'data', hint: 'Gate makro' },
    { to: '/sentimen', label: 'Sentimen', section: 'data', hint: 'Fear & Greed' },
    { to: '/likuidasi', label: 'Likuidasi', section: 'data', hint: 'Gate zona likuidasi' },
    { to: '/dcd-explorer', label: 'DCD Explorer', section: 'nevi', hint: 'Browse produk DCD' },
    { to: '/candidates', label: 'Rekomendasi Nevi', section: 'nevi', hint: 'Hasil filter + gate' },
    { to: '/positions', label: 'Posisi', section: 'nevi', hint: 'Posisi DCD Binance' },
    { to: '/activity', label: 'Aktivitas Nevi', section: 'nevi', hint: 'Log analisis' },
    { to: '/atr-analysis', label: 'ATR', section: 'nevi', hint: 'Simulasi multiple' },
    { to: '/settings', label: 'Pengaturan', section: 'config', hint: 'Key & threshold Nevi' },
    { to: '/history', label: 'Riwayat', section: 'config', hint: 'Timeline' },
    { to: '/dashboard-v3', label: 'UI v3 ref', section: 'ref', hint: 'Tema referensi' },
  ] as const,

  dashboard: {
    title: 'Ringkasan desk',
    sub: `${brand.company} · ${brand.ai} memantau market & gate DCD`,
    cta: 'Biarkan Nevi analisis',
    ctaRunning: 'Nevi sedang menganalisis…',
    statusRunning: 'Nevi sedang menganalisis…',
    statusHasPass: (n: number) => `${n} rekomendasi Nevi lolos (cycle terakhir)`,
    statusNone: 'Tidak ada yang lolos filter di cycle terakhir',
    statusEmpty: 'Belum ada hasil analisis Nevi',
    hintEmpty: `Klik “${'Biarkan Nevi analisis'}” untuk scan DCD BTC/ETH → filter gate → rekomendasi.`,
    linkCandidates: 'Buka Rekomendasi Nevi (detail lolos/tolak)',
    emptyRec: 'Belum ada yang lolos. Minta Nevi analisis, lalu cek Rekomendasi Nevi.',
    emptyLog: 'Belum ada log. Minta Nevi analisis untuk mengisi langkah.',
  },

  candidates: {
    title: 'Rekomendasi Nevi',
    sub: 'Hasil filter cycle terakhir · chip gate per kandidat',
  },

  activity: {
    title: 'Aktivitas Nevi',
    sub: 'Trail audit tiap siklus analisis — live saat berjalan',
    empty: 'Belum ada aktivitas. Minta Nevi analisis dari Ringkasan.',
  },

  settings: {
    title: 'Pengaturan Nevi',
    sub: `Threshold, kredensial & jadwal · ${brand.company}`,
  },

  login: {
    redirect: `Mengalihkan ke ${brand.company} SSO…`,
  },
} as const;

export const MOBILE_NAV = [
  { to: '/', label: copy.nav.home, icon: 'grid' as const },
  { to: '/market', label: copy.nav.market, icon: 'chart' as const },
  { to: '/candidates', label: copy.nav.nevi, icon: 'nevi' as const },
  { to: '/positions', label: copy.nav.positions, icon: 'wallet' as const },
  { to: '/settings', label: copy.nav.settings, icon: 'gear' as const },
] as const;

export const MOBILE_TAB_PATHS = MOBILE_NAV.map((n) => n.to);
